import { CellulaFormSubmitData, Flex, Form, InputRadio, InputText, useFormContext } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { TopBar } from '@component/top_bar.component';
import { withTranslation } from '@application/i18n/with_translation';
import { useResolveController, withNewContainer } from '@visiba/instantiation';
import { ButtonArrange } from '@component/button_arrange.component';
import { Title } from '@component/title.component';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { validateNin } from '@application/validators';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { ViewRedRobinAnalyticActions, ViewRedRobinController } from '../view_red_robin.controller';
import { ScreenWhoIsRespondantController } from './screen_who_is_respondant.controller';

export const ScreenWhoIsRespondant = withNewContainer(function WhoIsRespondant() {
	const controller = useResolveController(ScreenWhoIsRespondantController);
	const analytic = useAnalyticContext();
	const viewData = controller.viewData.value;

	function handleSubmit(data: CellulaFormSubmitData) {
		controller.submit({
			respondentType: data.respondentType === 'representative' ? 'representative' : 'me',
			patientNin: data.patientNin ?? null,
			dateOfBirth: data.dateOfBirth ?? null,
		});
	}

	function handleInvalidSubmit() {
		analytic.trackEvent(ViewRedRobinAnalyticActions.FormIsInvalid);
	}

	if (viewData.ninPlaceholder === '') {
		return null;
	}

	return (
		<>
			<TopBar.Content />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Form.Root
					options={{
						defaultValues: {
							respondentType: 'me',
						},
						mode: 'onBlur',
					}}
					onSubmit={handleSubmit}
					onInvalid={handleInvalidSubmit}
				>
					<Flex grow={1} gap={tokens.spacing.x2}>
						<Title title={withTranslation('View.Booking.ScreenWhoIsRespondant.Title')} titleSize='small' />
						<WhoIsRespondantFormInputs />
					</Flex>

					<ButtonArrange>
						<Form.SubmitButton onClick={() => analytic.trackEvent('Clicked next button')}>
							{withTranslation('PW.Common.Next')}
						</Form.SubmitButton>
					</ButtonArrange>
				</Form.Root>
			</Flex>
		</>
	);
});

function WhoIsRespondantFormInputs() {
	const viewController = useResolveController(ViewRedRobinController);
	const controller = useResolveController(ScreenWhoIsRespondantController);
	const analytic = useAnalyticContext();
	const formContext = useFormContext();
	const respondentType = formContext?.control.watch('respondentType');

	const viewData = controller.viewData.value;
	const flowData = viewController.getFlowData();

	function handleRespondentTypeChange() {
		const value = formContext?.control.getValues('respondentType');
		analytic.trackEvent(`Changed "RespondentType" to "${value}"`);
	}

	function handlePatientNinChange() {
		analytic.trackEvent(`Changed "PatientNin"`);
	}

	return (
		<Flex gap={tokens.spacing.x2}>
			<InputRadio.Root
				label={withTranslation('View.Booking.ScreenWhoIsRespondant.Input.Label.WhoIsRespondant')}
				variant='outlined'
				required
				name='respondentType'
				options={{
					required: I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.ValidationError.Required.WhoIsRespondant'),
				}}
				onChange={handleRespondentTypeChange}
			>
				<InputRadio.Item label={withTranslation('View.Booking.ScreenWhoIsRespondant.Option.Me')} value='me' />
				<InputRadio.Item label={withTranslation('View.Booking.ScreenWhoIsRespondant.Option.Representative')} value='representative' />
			</InputRadio.Root>
			{respondentType === 'representative' ? (
				<>
					<InputText
						label={viewData.useNhsNumber ? withTranslation('Form.Input.Label.NhsNumber') : withTranslation('Form.Input.Label.Nin')}
						name='patientNin'
						required
						description={
							viewData.useNhsNumber
								? withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNhsNumber.Description')
								: withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNin.Description')
						}
						placeholder={viewData.ninPlaceholder}
						onBlur={handlePatientNinChange}
						options={{
							required: viewData.useNhsNumber
								? I18nAndL10n.translate('Form.ValidationError.Required.NhsNumber')
								: I18nAndL10n.translate('Form.ValidationError.Required.Nin'),
							validate: {
								nin: (value) => {
									return (
										validateNin(flowData.currentCountry, value) ||
										(viewData.useNhsNumber
											? I18nAndL10n.translate('Form.ValidationError.Format.NhsNumber')
											: I18nAndL10n.translate('Form.ValidationError.Format.Nin'))
									);
								},
							},
						}}
					/>
					{!viewData.countryNinHasAgeSupport ? (
						<InputText
							options={{
								required: withTranslation('Form.ValidationError.Required.DateOfBirth'),
								validate: {
									dateOfBirth: (value) => {
										return !isNaN(new Date(value).getTime());
									},
								},
							}}
							name='dateOfBirth'
							label={withTranslation('Form.Input.Label.DateOfBirth')}
						/>
					) : null}
				</>
			) : null}
		</Flex>
	);
}
